import { useEffect } from "react";
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  Switch,
  Text,
  Alert,
  Badge,
  LoadingOverlay,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconInfoCircle } from "@tabler/icons-react";
import { useProductTypesStore } from "./store";
import { notifications } from "@mantine/notifications";

const ATTRIBUTE_TYPES = [
  { value: "TEXT", label: "Text" },
  { value: "LONG_TEXT", label: "Long Text" },
  { value: "INTEGER", label: "Integer" },
  { value: "DECIMAL", label: "Decimal" },
  { value: "LIST", label: "List" },
  { value: "LIST_MULTIPLE_VALUES", label: "Multiple List" },
  { value: "MEDIA", label: "Media" },
  { value: "MEASUREMENT", label: "Measurement" },
];

const REQUIREMENT_LEVELS = [
  { value: "OPTIONAL", label: "Optional" },
  { value: "REQUIRED", label: "Required" },
];

export default function AttributeDialog({
  opened,
  onClose,
  attribute = null,
  marketplaceId,
  hierarchyCode = "",
}) {
  const { createAttribute, updateAttribute, loading } = useProductTypesStore();
  const isEdit = !!attribute;
  const isManualOverride = attribute?.origin === "manualOverride";

  const form = useForm({
    initialValues: {
      code: "",
      label: "",
      description: "",
      type: "TEXT",
      requirementLevel: "OPTIONAL",
      variant: false,
      example: "",
      defaultValue: "",
    },
    validate: {
      code: (value) => (!value ? "Code is required" : null),
      label: (value) => (!value ? "Label is required" : null),
      description: (value) => (!value ? "Description is required" : null),
    },
  });

  useEffect(() => {
    if (attribute) {
      form.setValues({
        code: attribute.code || "",
        label: attribute.label || "",
        description: attribute.description || "",
        type: attribute.type || "TEXT",
        requirementLevel: attribute.requirementLevel || "OPTIONAL",
        variant: attribute.variant || false,
        example: attribute.example || "",
        defaultValue: attribute.defaultValue || "",
      });
    } else {
      form.reset();
    }
  }, [attribute, opened]);

  const handleSubmit = async (values) => {
    try {
      const attributeData = {
        ...values,
        hierarchyCode: hierarchyCode,
      };

      if (isEdit) {
        await updateAttribute(marketplaceId, attribute.id, attributeData);
        notifications.show({
          title: "Success",
          message: "Attribute updated successfully",
          color: "green",
        });
      } else {
        await createAttribute(marketplaceId, attributeData);
        notifications.show({
          title: "Success",
          message: "Attribute created successfully",
          color: "green",
        });
      }
      onClose();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to save attribute",
        color: "red",
      });
    }
  };

  // For API attributes, show read-only view
  if (isEdit && !isManualOverride) {
    return (
      <Modal opened={opened} onClose={onClose} title="View Attribute" size="lg">
        <Stack gap="md">
          <Alert icon={<IconInfoCircle size="1rem" />} color="blue">
            This attribute was imported from the API and cannot be edited. Only
            manual override attributes can be modified.
          </Alert>

          <Group>
            <Text fw={500}>Origin:</Text>
            <Badge color="blue" variant="light">
              API
            </Badge>
          </Group>

          <TextInput label="Code" value={attribute.code} readOnly />

          <TextInput label="Label" value={attribute.label} readOnly />

          <Textarea
            label="Description"
            value={attribute.description}
            readOnly
            rows={3}
          />

          <Group grow>
            <TextInput label="Type" value={attribute.type} readOnly />
            <TextInput
              label="Requirement Level"
              value={attribute.requirementLevel}
              readOnly
            />
          </Group>

          {attribute.example && (
            <TextInput label="Example" value={attribute.example} readOnly />
          )}

          {attribute.defaultValue && (
            <TextInput
              label="Default Value"
              value={attribute.defaultValue}
              readOnly
            />
          )}

          <Group justify="flex-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? "Edit Attribute" : "Create Attribute"}
      size="lg"
    >
      <LoadingOverlay visible={loading} />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {isEdit && (
            <Group>
              <Text fw={500}>Origin:</Text>
              <Badge color="green" variant="light">
                Manual Override
              </Badge>
            </Group>
          )}

          <TextInput
            label="Code"
            placeholder="Enter attribute code"
            required
            disabled={isEdit} // Code cannot be changed in edit mode
            {...form.getInputProps("code")}
          />

          <TextInput
            label="Label"
            placeholder="Enter attribute label"
            required
            {...form.getInputProps("label")}
          />

          <Textarea
            label="Description"
            placeholder="Enter attribute description"
            required
            rows={3}
            {...form.getInputProps("description")}
          />

          <Group grow>
            <Select
              label="Type"
              data={ATTRIBUTE_TYPES}
              required
              {...form.getInputProps("type")}
            />

            <Select
              label="Requirement Level"
              data={REQUIREMENT_LEVELS}
              required
              {...form.getInputProps("requirementLevel")}
            />
          </Group>

          <Switch
            label="Is Variant"
            description="Whether this attribute is a variation axis"
            {...form.getInputProps("variant", { type: "checkbox" })}
          />

          <TextInput
            label="Example"
            placeholder="Enter an example value (optional)"
            {...form.getInputProps("example")}
          />

          <TextInput
            label="Default Value"
            placeholder="Enter default value (optional)"
            {...form.getInputProps("defaultValue")}
          />

          <Group justify="flex-end" mt="xl">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              {isEdit ? "Update" : "Create"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
